import intersectionWith from 'lodash-es/intersectionWith'
import uniq from 'lodash-es/uniq'
import type { CustomMaterial } from 'metalworks-domain'
import {
  ApplicationTypes,
  BendDirection,
  BendOrientation,
  Finish,
  FinishDirection,
  Handing,
  RawMaterial,
  attachComponent,
  finishLabels,
} from 'metalworks-domain'
import * as THREE from 'three'
import { match } from 'ts-pattern'

import type { StaticPerspectiveCameraComponentState } from '@salsita-ai/platform-project-sdk/domain'
import {
  assertNumber,
  attributeSelectionComponent,
  boundingSphereComponent,
  customPriceFooterComponent,
  customSceneEntityComponent,
  customSummaryPageComponent,
  detailComponent,
  finalizeButtonComponent,
  finalizePageComponent,
  fitRootPerspectiveCameraComponent,
  genericDimensionComponent,
  materialSwatchComponent,
  memoEval,
  propertyPageComponent,
  saveTemplateThumbnailComponent,
  sceneEntityCustomObjectComponent,
  sceneEntityPropertiesComponent,
} from '@salsita-ai/platform-project-sdk/domain'

import { ReplicadPlate } from '../canvas/ReplicadPlate'
import { createPlate, materialSwatches } from '../canvas/plate'
import { FeatureList } from '../ui/FeatureList'
import { PlateRecipes } from '../ui/PlateRecipes'
import { FinalizationPage } from '../ui/finalization/FinalizationPage'
import { FinalizeButton } from '../ui/finalization/FinalizeButton'
import { KickPlatePropertyPage } from '../ui/property-pages/KickPlatePropertyPage'
import { RectangularPlatePropertyPage } from '../ui/property-pages/RectangularPlatePropertyPage'
import { RoundPlatePropertyPage } from '../ui/property-pages/RoundPlatePropertyPage'
import { SquarePlatePropertyPage } from '../ui/property-pages/SquarePlatePropertyPage'
import { StrikePlatePropertyPage } from '../ui/property-pages/StrikePlatePropertyPage'
import { CustomPriceFooter } from '../ui/sidebar-footer'
import { SummaryPage } from '../ui/summary/SummaryPage'

// Enable for debugging STEP file generation.
const RENDER_REPLICAD_ON_SUMMARY_PAGE = false

export const getDefaultCameraProps = (
  bendDirection: BendDirection | null,
  bendOrientation: BendOrientation | null,
  handing: Handing,
  angleOffset: number
) => {
  const baseAzimuthAngle = handing === Handing.standard ? 0 : Math.PI

  const { polarOffset, azimuthOffset } = match({
    direction: bendDirection,
    orientation: bendOrientation,
  })
    .with({ orientation: BendOrientation.Horizontal, direction: BendDirection.Front }, () => ({
      polarOffset: -Math.PI / 4,
      azimuthOffset: 0,
    }))
    .with({ orientation: BendOrientation.Horizontal, direction: BendDirection.Back }, () => ({
      polarOffset: Math.PI / 4,
      azimuthOffset: 0,
    }))
    .with({ orientation: BendOrientation.Vertical, direction: BendDirection.Front }, () => ({
      polarOffset: 0,
      azimuthOffset: Math.PI / 4,
    }))
    .with({ orientation: BendOrientation.Vertical, direction: BendDirection.Back }, () => ({
      polarOffset: 0,
      azimuthOffset: -Math.PI / 4,
    }))
    .otherwise(() => ({
      polarOffset: 0,
      azimuthOffset: 0,
    }))

  const props = {
    fov: 35,
    far: 10000,
    near: 1,
    polarAngle: Math.PI / 2 - angleOffset + polarOffset,
    azimuthAngle: baseAzimuthAngle - angleOffset + azimuthOffset,
    // minAzimuthAngle: baseAzimuthAngle - Math.PI / 4 + azimuthOffset,
    // maxAzimuthAngle: baseAzimuthAngle + Math.PI / 4 + azimuthOffset,
    // minPolarAngle: Math.PI / 4 + polarOffset,
    // maxPolarAngle: (3 * Math.PI) / 4 + polarOffset,
    minDistance: 20,
    maxDistance: 5000,
  } as const satisfies Partial<StaticPerspectiveCameraComponentState>

  return props
}

export const plateComponents = [
  attachComponent
    .withComponent(customSummaryPageComponent, 'default')
    .withAlias('Plate')
    .attach(() => {
      // TODO: memoEval
      return {
        summaryNavigationState: ApplicationTypes.NavigationState.Summary,
        page: <SummaryPage />,
      }
    }),

  attachComponent
    .withComponent(finalizeButtonComponent, 'default')
    .withAlias('Plate')
    .attach(() => {
      // TODO: memoEval
      return {
        button: <FinalizeButton />,
      }
    }),

  attachComponent
    .withComponent(finalizePageComponent, 'default')
    .withAlias('Plate')
    .attach(() => {
      // TODO: memoEval
      return {
        finalizeNavigationStates: [
          ApplicationTypes.NavigationState.Finalization,
          ApplicationTypes.NavigationState.FinalizationThumbnail,
          ApplicationTypes.NavigationState.BulkFinalization,
        ],
        page: <FinalizationPage />,
      }
    }),

  attachComponent
    .withComponent(customPriceFooterComponent, 'default')
    .withAlias('Plate')
    .attach(() => {
      // TODO: memoEval
      return {
        node: <CustomPriceFooter />,
      }
    }),

  // TODO: yaml
  attachComponent
    .withComponent(detailComponent, 'default')
    .withAlias('Plate')
    .attach(() => {
      return {
        title: 'Plate',
        subtitle: '',
        description: '',
      }
    }),
  // TODO: yaml
  attachComponent
    .withComponent(genericDimensionComponent, 'cornerRadius')
    .withEntity('KickPlate')
    .withEntity('RectangularPlate')
    .withEntity('SquarePlate')
    .attach(
      s => ({
        value: s.cornerRadius,
        min: 0.5,
        max: Math.min(s.$sizeX, s.$sizeY) / 2,
        unit: 'mm',
      }),
      v => {
        return {
          cornerRadius: v.value,
        }
      }
    ),
  // TODO: yaml
  attachComponent
    .withComponent(genericDimensionComponent, 'diameter')
    .withEntity('RoundPlate')
    .attach(
      s => ({
        value: s.diameter,
        min: s.$minLength,
        max: Math.min(600, s.$maxRawSize.x, s.$maxRawSize.y),
        unit: 'mm',
      }),
      v => {
        return {
          diameter: v.value,
        }
      }
    ),
  // TODO: yaml
  attachComponent
    .withComponent(genericDimensionComponent, 'quantity')
    .withAlias('Plate')
    .attach(
      memoEval(
        s => [s.quantity],
        quantity => ({
          value: quantity,
          min: 1,
          max: 10000,
          unit: quantity === 1 ? 'pc' : 'pcs',
        })
      ),
      v => {
        return {
          quantity: v.value,
        }
      }
    ),
  // TODO: yaml
  attachComponent
    .withComponent(materialSwatchComponent, 'rawMaterial')
    .withAlias('Plate')
    .attach(
      s => ({
        title: 'Material',
        description: 'Select one of the available materials for the plate.',
        swatches: materialSwatches,
        selectedSwatchKey: s.rawMaterial,
      }),
      v => {
        return {
          rawMaterial: RawMaterial.is(v.selectedSwatchKey) ? v.selectedSwatchKey : undefined,
        }
      }
    ),
  // TODO: yaml
  attachComponent
    .withComponent(attributeSelectionComponent, 'finish')
    .withAlias('Plate')
    .attach(
      s => ({
        value: s.finish,
        // TODO: Move to an expression later. There's some issue with generated type.
        options: intersectionWith(
          [
            { label: finishLabels[Finish.brushed], value: Finish.brushed },
            { label: finishLabels[Finish.milled], value: Finish.milled },
            { label: finishLabels[Finish.chequer], value: Finish.chequer },
            { label: finishLabels[Finish.propeller], value: Finish.propeller },
          ],
          s.$customMaterials,
          (a, b) => a.value === (b as CustomMaterial).finish
        ),
      }),
      v => {
        Finish.assert(v.value)
        return { finish: v.value }
      }
    ),
  // TODO: yaml
  attachComponent
    .withComponent(attributeSelectionComponent, 'finishDirection')
    .withAlias('Plate')
    .attach(
      s => ({
        value: s.finishDirection,
        // TODO: Move to an expression later. There's some issue with generated type.
        options: [
          { label: 'Horizontal', value: FinishDirection.horizontal },
          { label: 'Vertical', value: FinishDirection.vertical },
        ],
      }),
      v => {
        FinishDirection.assert(v.value)
        return { finishDirection: v.value }
      }
    ),
  // TODO: yaml
  attachComponent
    .withComponent(attributeSelectionComponent, 'handing')
    .withAlias('Plate')
    .attach(
      s => ({
        value: s.handing,
        // TODO: Move to an expression later. There's some issue with generated type.
        options: [
          { label: 'Standard', value: Handing.standard },
          { label: 'Reverse', value: Handing.reverse },
        ],
      }),
      v => {
        Handing.assert(v.value)
        return { handing: v.value }
      }
    ),
  // TODO: yaml
  attachComponent
    .withComponent(attributeSelectionComponent, 'thickness')
    .withAlias('Plate')
    .attach(
      s => ({
        value: s.thickness,
        // TODO: Move to an expression later. There's some issue with generated type.
        options: uniq(s.$customMaterials.map(({ thickness }) => thickness))
          .sort((a, b) => a - b)
          .map(thickness => ({
            label: thickness.toFixed(2) + ' mm',
            value: thickness,
          })),
      }),
      v => {
        assertNumber(v.value)
        return { thickness: v.value }
      }
    ),
  attachComponent
    .withEntity('KickPlate')
    .withComponent(propertyPageComponent, 'dimensions')
    .withFilter({
      navigationStates: [ApplicationTypes.NavigationState.Dimensions],
    })
    // TODO: memoEval
    .attach(s => {
      if (!s.$selectedCustomMaterial) {
        throw new Error('Custom material not found. This should happen only in dry-run mode.')
      }

      return {
        page: (
          <KickPlatePropertyPage
            id={s.id}
            standard={s.$selectedCustomMaterial.standard}
            hasFinishDirection={s.$hasFinishDirection}
            powderCoating={s.powderCoating}
          />
        ),
      }
    }),
  attachComponent
    .withEntity('RectangularPlate')
    .withComponent(propertyPageComponent, 'dimensions')
    .withFilter({
      navigationStates: [ApplicationTypes.NavigationState.Dimensions],
    })
    // TODO: memoEval
    .attach(s => {
      if (!s.$selectedCustomMaterial) {
        throw new Error('Custom material not found. This should happen only in dry-run mode.')
      }

      return {
        page: (
          <RectangularPlatePropertyPage
            id={s.id}
            standard={s.$selectedCustomMaterial.standard}
            hasFinishDirection={s.$hasFinishDirection}
            powderCoating={s.powderCoating}
            $bend={s.$customBend}
          />
        ),
      }
    }),
  attachComponent
    .withEntity('RoundPlate')
    .withComponent(propertyPageComponent, 'dimensions')
    .withFilter({
      navigationStates: [ApplicationTypes.NavigationState.Dimensions],
    })
    // TODO: memoEval
    .attach(s => {
      if (!s.$selectedCustomMaterial) {
        throw new Error('Custom material not found. This should happen only in dry-run mode.')
      }

      return {
        page: (
          <RoundPlatePropertyPage
            id={s.id}
            standard={s.$selectedCustomMaterial.standard}
            hasFinishDirection={s.$hasFinishDirection}
            powderCoating={s.powderCoating}
            $bend={s.$customBend}
          />
        ),
      }
    }),
  attachComponent
    .withEntity('SquarePlate')
    .withComponent(propertyPageComponent, 'dimensions')
    .withFilter({
      navigationStates: [ApplicationTypes.NavigationState.Dimensions],
    })
    // TODO: memoEval
    .attach(s => {
      if (!s.$selectedCustomMaterial) {
        throw new Error('Custom material not found. This should happen only in dry-run mode.')
      }

      return {
        page: (
          <SquarePlatePropertyPage
            id={s.id}
            standard={s.$selectedCustomMaterial.standard}
            hasFinishDirection={s.$hasFinishDirection}
            powderCoating={s.powderCoating}
            $bend={s.$customBend}
          />
        ),
      }
    }),

  attachComponent
    .withEntity('StrikePlate')
    .withComponent(propertyPageComponent, 'dimensions')
    .withFilter({
      navigationStates: [ApplicationTypes.NavigationState.Dimensions],
    })
    // TODO: memoEval
    .attach(s => {
      if (!s.$selectedCustomMaterial) {
        throw new Error('Custom material not found. This should happen only in dry-run mode.')
      }

      return {
        page: (
          <StrikePlatePropertyPage
            id={s.id}
            standard={s.$selectedCustomMaterial.standard}
            hasFinishDirection={s.$hasFinishDirection}
            powderCoating={s.powderCoating}
            hasBend={s.hasBend}
            shapeType={s.shapeType}
          />
        ),
      }
    }),
  attachComponent
    .withAlias('Plate')
    .withComponent(propertyPageComponent, 'features')
    .withFilter({
      navigationStates: [
        ApplicationTypes.NavigationState.Features,
        ApplicationTypes.NavigationState.Finalization,
        ApplicationTypes.NavigationState.FinalizationThumbnail,
      ],
    })
    // TODO: memoEval
    .attach(s => {
      return {
        page: <PlateRecipes entityId={s.id} />,
      }
    }),
  // TODO: yaml
  attachComponent
    .withAlias('Plate')
    .withComponent(propertyPageComponent, 'FeatureList')
    .withFilter({
      navigationStates: [ApplicationTypes.NavigationState.FeatureList],
    })
    .attach(s => {
      return {
        page: <FeatureList entityId={s.id} />,
        activeNavigationStates: [ApplicationTypes.NavigationState.FeatureList],
      }
    }),
  attachComponent
    .withAlias('Plate')
    .withComponent(fitRootPerspectiveCameraComponent, 'default')
    // .withFilter({
    //   navigationStates: [ApplicationTypes.NavigationState.Dimensions, ApplicationTypes.NavigationState.Finalization],
    // })
    .attach(
      memoEval(
        s => [s.$activeBend?.direction ?? null, s.$activeBend?.orientation ?? null, s.handing],
        (bendDirection, bendOrientation, handing) => ({
          ...getDefaultCameraProps(bendDirection, bendOrientation, handing, 0),
          priority: 2,
          // fitMeshChanges: true,
        })
      )
    ),
  attachComponent
    .withAlias('Plate')
    .withComponent(saveTemplateThumbnailComponent, 'default')
    .attach(
      memoEval(
        s => [s.$activeBend?.direction ?? null, s.$activeBend?.orientation ?? null, s.handing],
        (bendDirection, bendOrientation, handing) => {
          const defaultCameraProps = getDefaultCameraProps(bendDirection, bendOrientation, handing, 0)

          return {
            fov: defaultCameraProps.fov,
            near: defaultCameraProps.near,
            far: defaultCameraProps.far,
            azimuthAngle: defaultCameraProps.azimuthAngle,
            polarAngle: defaultCameraProps.polarAngle,
          }
        }
      )
    ),
  // TODO: yaml
  // attachComponent
  //   .withAlias('Plate')
  //   .withComponent(staticPerspectiveCameraComponent, 'default')
  //   .withFilter({
  //     navigationStates: [
  //       ApplicationTypes.NavigationState.Features,
  //       ApplicationTypes.NavigationState.FeatureList,
  //       ApplicationTypes.NavigationState.FinalizationThumbnail,
  //       ApplicationTypes.NavigationState.Summary,
  //     ],
  //   })
  //   .attach(
  //     memoEval(
  //       s => [s.$activeBend?.direction ?? null, s.$activeBend?.orientation ?? null, s.handing],
  //       (bendDirection, bendOrientation, handing) => ({
  //         ...getDefaultCameraProps(bendDirection, bendOrientation, handing, Math.PI / 16),
  //         priority: 1,
  //       })
  //     )
  //   ),
  attachComponent
    .withAlias('Plate')
    .withComponent(boundingSphereComponent, 'default')
    // TODO: memoEval
    .attach(
      memoEval(
        s => [s.$sizeX, s.$sizeY],
        (sizeX, sizeY) => {
          const radius = Math.sqrt(sizeX * sizeX + sizeY * sizeY) * 0.6
          return {
            shape: new THREE.Sphere(new THREE.Vector3(0, 0, 0), radius),
          }
        }
      )
    ),
  attachComponent
    .withAlias('Plate')
    .withComponent(sceneEntityCustomObjectComponent, 'Plate3D')
    .withFilter({
      viewStates: [ApplicationTypes.ViewState['3d']],
    })
    // TODO: memoEval
    .attach(s => ({
      object: createPlate(s, true),
    })),
  attachComponent
    .withAlias('Plate')
    .withComponent(sceneEntityCustomObjectComponent, 'Plate2D')
    .withFilter({
      viewStates: [ApplicationTypes.ViewState['2d']],
      // Uncomment to render Replicad version of the plate on all pages except Summary.
      ...(RENDER_REPLICAD_ON_SUMMARY_PAGE
        ? {
            navigationStates: ApplicationTypes.NavigationState.values().filter(
              state => state !== ApplicationTypes.NavigationState.Summary
            ),
          }
        : {}),
    })
    // TODO: memoEval
    .attach(s => ({
      object: createPlate(s, false),
    })),
  // TODO: yaml
  attachComponent
    .withAlias('Plate')
    .withComponent(sceneEntityPropertiesComponent, 'default')
    .attach(s => ({
      offsetX: -s.$sizeX / 2,
      offsetY: -s.$sizeY / 2,
      offsetZ: -s.thickness / 2,
    })),

  /**
   * Enable to render Replicad version of the plate on Summary.
   * Don't forget to hide the other `sceneEntityCustomObjectComponent` on Summary.
   */
  ...(RENDER_REPLICAD_ON_SUMMARY_PAGE
    ? [
        attachComponent
          .withAlias('Plate')
          .withComponent(customSceneEntityComponent, 'Replicad')
          .withFilter({
            navigationStates: [ApplicationTypes.NavigationState.Summary],
          })
          .attach(() => ({
            node: <ReplicadPlate />,
          })),
      ]
    : []),
]
